import { Col, Form, Modal, ModalProps, Row, TableProps } from 'antd';
import { ColumnType } from 'antd/es/table';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Switch } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { Result } from 'interfaces/SalesInfo';
import formatNumber, { formatCurrency } from 'utils/constants/functions';

export type IUpdatePromotionData = Pick<
  Result,
  'ConditionName' | 'ConditionNum' | 'ConditionType' | 'ConditionValue' | 'Type'
> & { records: (Result & { id: number })[] };

type IUpdateSAPPromotionModal = Omit<ModalProps, 'onOk'> & {
  tableProps?: TableProps;
  data: IUpdatePromotionData | undefined;
  onOk?(record: IUpdatePromotionData): void;
};

export const UpdateSAPPromotionModal = memo(
  ({ open, tableProps, data, onCancel, onOk }: IUpdateSAPPromotionModal) => {
    const [records, setRecords] = useState<IUpdatePromotionData['records']>(),
      [pageInfo, setPageInfo] = useState({
        page: 1,
        pageSize: 10,
      } as {
        page: number;
        pageSize: number;
      });

    useEffect(() => {
      setRecords(data?.records);
    }, [data]);

    const handleSwitchApplyPromotion = (record: any) => {
      if (!records) return;

      setRecords(
        records.map((item) => {
          if (record.id === item.id) return { ...item, apply: !item.apply };
          return item;
        }),
      );
    };

    const columns = useMemo(() => {
      return [
        {
          title: 'STT',
          width: 50,
          render: (_: any, __: any, index: number) =>
            index + 1 + pageInfo.pageSize * (pageInfo.page - 1),
        },
        {
          title: 'Mã chương trình',
          width: 150,
          dataIndex: 'ConditionNum',
          render: (value, record) => {
            return `${value} - ${record?.Item}`;
          },
        },
        { title: 'Chương trình', dataIndex: 'ConditionName', width: 150 },
        {
          title: 'ConditionType',
          dataIndex: 'ConditionType',
          width: 150,
          render: (value, record) => {
            return record?.Type?.toLowerCase() === 'percent'
              ? `${value} - %`
              : `${value} - giá trị`;
          },
        },
        {
          title: 'Giá trị/phần trăm CK',
          width: 150,
          dataIndex: 'ConditionValue',
          align: 'right',
          currency: 'vi-VN',
          render: (value, record) => {
            try {
              const [newValue, ..._] = value.split('-');

              return record?.Type?.toLowerCase() === 'percent'
                ? formatNumber(newValue)
                : formatCurrency({ value: newValue, round: true });
            } catch {
              return '-';
            }
          },
        },
        {
          title: 'Đơn vị',
          width: 100,
          render: (_, record) => {
            return record.Type === 'Percent'
              ? '%'
              : (record?.Currency ?? 'VND');
          },
        },
        {
          title: 'Sản phẩm áp dụng',
          width: 250,
          dataIndex: 'Material',
        },
        {
          title: 'Serial',
          width: 150,
          render: () => '-',
        },
        {
          title: 'Ghi chú',
          width: 350,
          dataIndex: 'note',
          render: () => '-',
        },
        {
          title: 'Thời gian hiệu lực',
          width: 150,
          render: () => '-',
        },
        {
          title: 'Thao tác',
          fixed: 'right' as const,
          width: 100,
          align: 'center',
          key: 'action',
          dataIndex: 'apply',
          render: (value, record) => {
            return (
              <Switch
                checked={value}
                onChange={() => handleSwitchApplyPromotion(record)}
              />
            );
          },
        },
      ] as ColumnType<any>[];
    }, [records, pageInfo]);

    const handleOk = useCallback(() => {
      if (!records) return;
      onOk && onOk({ ...data, records });
    }, [records]);

    return (
      <Modal
        title={data?.ConditionName}
        open={open}
        className="!w-[1000px]"
        onCancel={onCancel}
        footer={() => (
          <div className="flex justify-end mt-4 gap-2">
            <Button
              handleOnclick={onCancel}
              customClass="!border-transparent !text-neutral-30 !bg-transparent"
              title="Quay lại"
            />
            <Button handleOnclick={handleOk} title="Xác nhận" fill />
          </div>
        )}
      >
        <Row gutter={[16, 16]}>
          <Col>ConditionType:</Col>
          <Col>
            <p className="font-bold">
              {data?.ConditionType} -{' '}
              {data?.Type?.toLowerCase() === 'percent' ? '%' : 'vnd'}
            </p>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col>Giá trị/phần trăm CK:</Col>
          <Col>
            <p className="font-bold">
              {data?.Type?.toLowerCase() === 'percent'
                ? Number(data?.ConditionValue?.split('-')[0])
                : formatCurrency({
                    value: Number(data?.ConditionValue?.split('-')[0]),
                    round: true,
                  })}{' '}
              - {data?.Type?.toLowerCase() === 'percent' ? '%' : 'vnd'}
            </p>
          </Col>
        </Row>
        <Form layout="vertical" className="mt-4">
          <TableComponent
            columns={columns}
            dataSource={records}
            rowKey={(record) => record?.id ?? ''}
            scroll={{ x: 2000 }}
            {...tableProps}
            pagination={{
              current: pageInfo.page,
              pageSize: pageInfo.pageSize,
              onChange(page, pageSize) {
                setPageInfo({ pageSize, page });
              },
            }}
          />
        </Form>
      </Modal>
    );
  },
);
